import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';



// Thunk asynchrone pour fetch le JSON depuis le back
export const fetchJsonFromBack = createAsyncThunk(
    'json/fetchJsonFromBack',
    async ({ folderName, filename }) => {
        try {
            // Nettoyer les extensions .json en double
            const cleanFolderName = folderName ? folderName.replace(/\.json$/, '') : '';
            const cleanFilename = filename ? filename.replace(/\.json$/, '') : '';

            if (!cleanFolderName) {
                console.error('[fetchJsonFromBack] ERREUR : Le nom du dossier (folderName) est vide ou non défini. Impossible de construire le chemin du JSON.');
                throw new Error('Le nom du dossier (folderName) est vide ou non défini. Impossible de charger le JSON.');

            }
            if (!cleanFilename) {
                console.error('[fetchJsonFromBack] ERREUR : Le nom du fichier (filename) est vide ou non défini. Impossible de construire le chemin du JSON.');
                throw new Error('Le nom du fichier (filename) est vide ou non défini. Impossible de charger le JSON.');
            }

            const url = `/api/data/${cleanFolderName}/${cleanFilename}.json`;

            const response = await axios.get(url, { withCredentials: true });

            return response.data;
        } catch (error) {
            console.error('Erreur complète:', error);

            if (error.response) {
                console.error('Status:', error.response.status);
                console.error('Data:', error.response.data);
            }

            if (error.request) {
                console.error('Requête envoyée mais pas de réponse');
            }

            console.error('URL tentée:', error.config?.url);

            throw error;
        }
    }
);

// Thunk asynchrone pour sauvegarder les données vers lebackend
export const saveJsonToBack = createAsyncThunk(
    'json/saveJsonToBack',
    async (updatedData, { getState }) => {
        try {
            const state = getState();
            const existingData = state.json.jsonData || {};

            // Fusion profonde
            const mergedData = JSON.parse(JSON.stringify(existingData));

            // Mise à jour des propriétés de premier niveau (hors settings et process)
            Object.keys(updatedData).forEach(key => {
                if (key !== 'settings' && key !== 'process') {
                    mergedData[key] = updatedData[key];
                }
            });

            // Fusionner les settings
            if (updatedData.settings) {
                mergedData.settings = {
                    ...(mergedData.settings || {}),
                    ...updatedData.settings
                };
            }

            // Fusionner le process
            if (updatedData.process) {
                const existingProcess = JSON.parse(JSON.stringify(mergedData.process || {}));
                const updatedProcess = updatedData.process;

                const newProcess = { ...existingProcess };

                Object.keys(updatedProcess).forEach(key => {
                    if (key !== 'steps' && key !== 'stages') {
                        newProcess[key] = updatedProcess[key];
                    }
                });

                newProcess.steps = Array.isArray(updatedProcess.steps)
                    ? [...updatedProcess.steps]
                    : (Array.isArray(existingProcess.steps) ? [...existingProcess.steps] : []);

                newProcess.stages = Array.isArray(updatedProcess.stages)
                    ? [...updatedProcess.stages]
                    : (Array.isArray(existingProcess.stages) ? [...existingProcess.stages] : []);

                mergedData.process = newProcess;
            }

            // Récupérer le chemin du dossier
            let folderPath = state.json.folderName || window.location.pathname.split('/').pop();
            if (mergedData.process?.urlFolderName) folderPath = mergedData.process.urlFolderName;

            if (!folderPath) throw new Error("Le chemin du dossier n'est pas défini");

            // Décoder si encodé (%20 etc.) puis remplacer les espaces par des underscores
            // Pour l'URL (underscores)
            const folderPathWithoutExt = decodeURIComponent(folderPath)
                .replace(/\.json$/, '')
                .replace(/\s+/g, '_');

            // Pour le chemin réel sur le disque (espaces restaurés)
            const folderPathReal = decodeURIComponent(folderPath)
                .replace(/\.json$/, '');

            const filename = folderPathReal.split('/').pop() + '.json';
            const completePath = `/api/data/${folderPathReal}/${filename}`;

            // PUT vers le backend
            const response = await axios.put(completePath, mergedData, { withCredentials: true });
            console.log('✅ Données sauvegardées avec succès vers le backend :', completePath);

            // Construire le newContent avec le parent folder si existant
            const authUser = JSON.parse(localStorage.getItem("authUser"));
            const userEmail = authUser?.user_EMAIL;

            let newContent = '';
            if (userEmail) {
                // Extraire le suffixe langue (ex: "_FR") pour trouver le parent
                const langSuffixMatch = folderPathWithoutExt.match(/^(.+)_([A-Z]{2})$/);
                const parentFolder = langSuffixMatch ? langSuffixMatch[1] : '';

                // Restaurer les espaces pour le chemin backend (underscores → espaces)
                // Restaurer les espaces uniquement pour le parent
                const toBackendPath = (str) => str.replace(/_/g, ' ');

                newContent = parentFolder
                    ? `${userEmail}/${toBackendPath(parentFolder)}/${toBackendPath(parentFolder)}_${langSuffixMatch[2]}`
                    : `${userEmail}/${toBackendPath(folderPathWithoutExt)}`;

                // POST pour rafraîchir les contenus du créateur automatiquement apres la sauvegarde
                /* const refreshResponse = await axios.post(
                    "/api/headset/refresh-creator-contents",
                    { userEmail, newContent },
                    { withCredentials: true }
                );
                window.dispatchEvent(new Event("refreshHeadsets")); */

                //console.log("✅ Contenus du créateur mis à jour :", refreshResponse.data);
                console.log("newContent:", newContent);
            } else {
                console.warn("⚠️ Email introuvable dans authUser");
            }

            return response.data || mergedData;
        } catch (error) {
            console.error('❌ Erreur lors de la sauvegarde (JsonSlice) :', error);
            throw error;
        }
    }
);

const initialState = {
    jsonData: {
        usage: "File of all the steps and actions related to a procedure. Contains all the operations that can be carried out, organized into stages, steps and actions.",
        settings: {
            contentVersion: "",
            ownerName: "",
            ownerIdentifier: "",
            appVersion: "0.1",
            chapter: "",
            calibrateAfterStageID: 0,
            unit: "mm",
            langCode: "FR"
        },
        process: {
            processTitle: "",
            processDesc: "",
            workspaceShape: "",
            workspaceShapeSize: { x: 0, y: 0, z: 0 },
            workspaceShapeRot: { x: 0, y: 0, z: 0 }, // y and z are unused
            workspaceOffsetPos: { x: 0, y: 0, z: 0 }, // half of the shape size
            workspaceOffsetRot: { x: 0, y: 0, z: 0 }, // unused
            mainHologram: "",
            steps: [],
            stages: []
        }
    },
    loading: false,
    error: null,
    folderName: null,
    filename: null
};

const jsonSlice = createSlice({
    name: 'json',
    initialState,
    reducers: {
        initializeJson: (state) => {
            state.jsonData = {
                usage: "File of all the steps and actions related to a procedure. Contains all the operations that can be carried out, organized into stages, steps and actions.",
                settings: {
                    contentVersion: "",
                    ownerName: "",
                    ownerIdentifier: "",
                    appVersion: "",
                    chapter: "",
                    calibrateAfterStageID: null,
                    unit: "mm",
                    langCode: "FR"
                },
                process: {
                    processTitle: "",
                    processDesc: "",
                    workspaceShape: "",
                    workspaceShapeSize: { x: null, y: null, z: null },
                    workspaceShapeRot: { x: null, y: null, z: null },
                    workspaceOffsetPos: { x: null, y: null, z: null },
                    workspaceOffsetRot: { x: null, y: null, z: null },
                    mainHologram: "",
                    steps: [],
                    stages: []
                }
            };
        },
        initializeJsonFromFile: (state, action) => {
            state.jsonData = action.payload;
        },
        updateJsonData: (state, action) => {
            state.jsonData = action.payload;
        },
        updateSettingsField: (state, action) => {
            if (!state.jsonData) {
                state.jsonData = {
                    settings: {},
                    process: {}
                };
            }
            if (!state.jsonData.settings) {
                state.jsonData.settings = {};
            }
            state.jsonData.settings[action.payload.field] = action.payload.value;
        },
        updateProcessField: (state, action) => {
            if (!state.jsonData) {
                state.jsonData = {
                    settings: {},
                    process: {}
                };
            }
            if (!state.jsonData.process) {
                state.jsonData.process = {};
            }
            state.jsonData.process[action.payload.field] = action.payload.value;
        },
        addOrUpdateStep: (state, action) => {
            try {
                const { stepID, data } = action.payload;
                if (!state.jsonData.process) {
                    state.jsonData.process = {
                        steps: [],
                        stages: []
                    };
                }
                if (!state.jsonData.process.steps) {
                    state.jsonData.process.steps = [];
                }
                const existingStepIndex = state.jsonData.process.steps.findIndex(step => step?.stepID === stepID);
                if (existingStepIndex !== -1) {
                    state.jsonData.process.steps[existingStepIndex] = data;
                } else {
                    state.jsonData.process.steps.push(data);
                }
            } catch (error) {
                console.error('Erreur dans addOrUpdateStep:', error);
                throw error;
            }
        },
        addStage: (state, action) => {
            const stage = action.payload;

            // S'assurer que jsonData existe
            if (!state.jsonData) {
                state.jsonData = {
                    settings: {},
                    process: {}
                };
            }

            // S'assurer que process existe
            if (!state.jsonData.process) {
                state.jsonData.process = {};
            }

            // S'assurer que stages est un tableau
            if (!Array.isArray(state.jsonData.process.stages)) {
                state.jsonData.process.stages = [];
            }

            // Vérifier si le stage existe déjà
            const existingIndex = state.jsonData.process.stages.findIndex(s => s.stageID === stage.stageID);

            if (existingIndex >= 0) {
                // Mise à jour d'un stage existant
                state.jsonData.process.stages[existingIndex] = stage;
            } else {
                // Ajout d'un nouveau stage
                state.jsonData.process.stages.push(stage);
            }
        },

        // Ajouter ou mettre à jour une action dans une étape
        addOrUpdateAction: (state, action) => {
            const { stepId, actionData } = action.payload;

            // S'assurer que jsonData existe
            if (!state.jsonData || !state.jsonData.process || !Array.isArray(state.jsonData.process.steps)) {
                console.error('Structure de données invalide pour ajouter une action');
                return;
            }

            // Trouver l'étape correspondante
            const stepIndex = state.jsonData.process.steps.findIndex(step => step.stepID === stepId);

            if (stepIndex >= 0) {
                const step = { ...state.jsonData.process.steps[stepIndex] };

                // Initialiser le tableau stepActions s'il n'existe pas
                if (!step.stepActions) {
                    step.stepActions = [];
                }

                // Vérifier si l'action existe déjà
                const actionIndex = step.stepActions.findIndex(
                    a => a.feedbackItemTemplateID === actionData.feedbackItemTemplateID
                );

                if (actionIndex >= 0) {
                    // Mise à jour d'une action existante
                    step.stepActions[actionIndex] = actionData;
                } else {
                    // Ajout d'une nouvelle action
                    step.stepActions.push(actionData);
                }

                // Mettre à jour l'étape dans le state
                state.jsonData.process.steps[stepIndex] = step;
            }
        },

        // Supprimer une action d'une étape
        removeAction: (state, action) => {
            const { stepId, actionId } = action.payload;

            // S'assurer que jsonData existe
            if (!state.jsonData || !state.jsonData.process || !Array.isArray(state.jsonData.process.steps)) {
                console.error('Structure de données invalide pour supprimer une action');
                return;
            }

            // Trouver l'étape correspondante
            const stepIndex = state.jsonData.process.steps.findIndex(step => step.stepID === stepId);

            if (stepIndex >= 0) {
                const step = { ...state.jsonData.process.steps[stepIndex] };

                if (step.stepActions && Array.isArray(step.stepActions)) {
                    // Filtrer pour supprimer l'action
                    step.stepActions = step.stepActions.filter(
                        action => action.feedbackItemTemplateID !== actionId
                    );

                    // Mettre à jour l'étape dans le state
                    state.jsonData.process.steps[stepIndex] = step;
                }
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchJsonFromBack.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchJsonFromBack.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;

                // Vérifier si les données reçues sont valides
                if (!action.payload) {
                    console.log('Aucune donnée reçue du serveur');
                    return;
                }

                // Préparer les données existantes
                const existingData = state.jsonData || {};
                const existingSettings = existingData.settings || {};
                const existingProcess = existingData.process || {};

                // Préparer les nouvelles données
                const newData = action.payload || {};
                const newSettings = newData.settings || {};
                const newProcess = newData.process || {};

                // Fusionner les données
                state.jsonData = {
                    // Fusionner les paramètres
                    settings: {
                        ...existingSettings,
                        ...newSettings
                    },
                    // Fusionner le process
                    process: {
                        ...existingProcess,
                        ...newProcess,
                        // S'assurer que steps et stages sont des tableaux
                        steps: Array.isArray(newProcess.steps)
                            ? newProcess.steps
                            : (Array.isArray(existingProcess.steps) ? existingProcess.steps : []),
                        stages: Array.isArray(newProcess.stages)
                            ? newProcess.stages
                            : (Array.isArray(existingProcess.stages) ? existingProcess.stages : [])
                    },
                    // Conserver les autres propriétés existantes
                    ...existingData,
                    // Écraser avec les nouvelles propriétés
                    ...Object.keys(newData)
                        .filter(key => !['settings', 'process'].includes(key))
                        .reduce((obj, key) => {
                            obj[key] = newData[key];
                            return obj;
                        }, {})
                };
            })
            .addCase(fetchJsonFromBack.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(saveJsonToBack.pending, (state) => {
                state.loading = true;
            })
            .addCase(saveJsonToBack.fulfilled, (state, action) => {
                state.loading = false;
                console.log('✅ Sauvegarde réussie !');
                console.log('✅ Données sauvegardées :', action.payload);
            })
            .addCase(saveJsonToBack.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            });
    }
});


export const {
    initializeJson,
    initializeJsonFromFile,
    updateJsonData,
    updateSettingsField,
    updateProcessField,
    addOrUpdateStep,
    addStage,
    addOrUpdateAction,
    removeAction,
} = jsonSlice.actions;
export const initialStateWork = initialState; // export initial state for export/import buttons
export default jsonSlice.reducer;

//Creators : Florian Cohen-Joly @3DDGAMES -- Under license